'use client';
import { useState, useEffect } from 'react';
import Header from '@/components/Header';
import SessionProvider from '@/components/SessionProvider';
import Image from 'next/image';
import LocalizedName from '@/components/LocalizedName';

export default function DonationsPage() {
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalAmount, setTotalAmount] = useState(0);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);

    useEffect(() => {
        fetchDonations();
    }, []);

    const fetchDonations = async () => {
        try {
            const response = await fetch('/api/donations');
            const data = await response.json();

            if (data.success) {
                setDonations(data.donations);
                const total = data.donations
                    .filter(donation => donation.type !== 'service') // Only count monetary donations
                    .reduce((sum, donation) => sum + (donation.amount || 0), 0);
                setTotalAmount(total);
            }
        } catch (error) {
            console.error('Error fetching donations:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount).replace(/\.00$/, '');
    };

    const groupAndMergeDonations = (donationsList) => {
        const grouped = {};

        // Group donations by user ID
        donationsList.forEach(donation => {
            const userId = donation.user?._id || donation.userId;
            if (!userId) return;

            if (!grouped[userId]) {
                grouped[userId] = {
                    _id: donation._id,
                    user: donation.user,
                    amount: 0,
                    services: [],
                    type: 'merged'
                };
            }

            // Sum amounts for monetary donations
            if (donation.type !== 'service' && donation.amount) {
                grouped[userId].amount += donation.amount;
            }

            // Collect services
            if (donation.type === 'service' && donation.service) {
                grouped[userId].services.push(donation.service);
            }
        });

        // Convert grouped object to array
        return Object.values(grouped);
    };

    const mergedDonations = groupAndMergeDonations(donations);

    if (loading) {
        return (
            <div className="donations-page">
                <div className="loading">Loading donations...</div>
            </div>
        );
    }

    return <>
        <div className="donations-page">
            <div className="container">
                <div className="donations-header">
                    <h1>Supporters & Donors</h1>
                    <p>ધોરણ ૧૦ (વર્ષ 2000–2001)ની સમગ્ર બેચ તરફથી, આપણે સૌ દિલથી કૃતજ્ઞતા વ્યક્ત કરીએ છીએ આપણા તે તમામ સહપાઠી મિત્રો પ્રત્યે, જેમણે 25 વર્ષના આ સ્નેહમિલન અને ગુરુવંદન કાર્યક્રમને ભવ્ય અને સ્મરણિય બનાવવા માટે ધન, સમય, ઊર્જા તથા પ્રાયોજક રૂપે નિઃસ્વાર્થ સહયોગ આપ્યો.</p>
                    <p>આ વિશેષ પ્રસંગના સફળ આયોજન માટે જેમણે આગળ આવી જવાબદારી સંભાળી, એવા દરેક મિત્રનો આપણે સૌ હૃદયપૂર્વક આભાર માનીએ છીએ. આ સહકાર આપણી મિત્રતાની ગાથામાં સદાય સન્માન સાથે ઝળહળતો રહેશે.</p>
                    <div className="qr-section">
                        <p className="qr-heading">આ યાદગાર સ્નેહમિલનને સફળ બનાવવા માટે <br /> આપનો સહયોગ આપવા QR કોડ સ્કેન કરો</p>
                        <div className="qr-card" role="button" tabIndex={0} aria-label="Open QR code modal" onClick={() => setIsQrModalOpen(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsQrModalOpen(true); }}>
                            <img
                                src="/img/ghg2001qrcode.jpeg"
                                alt="GHG2001 QR Code for Donations"
                                className="qr-image"
                            />
                        </div>
                        {isQrModalOpen && (
                            <div className="qr-modal-overlay" onClick={() => setIsQrModalOpen(false)}>
                                <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                                    <button className="qr-modal-close" onClick={() => setIsQrModalOpen(false)} aria-label="Close QR code">
                                        ×
                                    </button>
                                    <h2>SCAN THE QR CODE</h2>
                                    <Image
                                        src="/img/ghg2001qrcode.jpeg"
                                        alt="GHG2001 QR Code for Donations"
                                        width={420}
                                        height={420}
                                        className="qr-modal-image"
                                    />
                                    <p>તમારા મોબાઈલથી QR સ્કેન કરીને દાન કરો</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="total-amount">
                        <h2>Total Donations: {formatCurrency(totalAmount)}</h2>
                    </div>
                </div>

                {donations.length === 0 ? (
                    <div className="no-donations">
                        <p>No donations to display yet.</p>
                    </div>
                ) : (
                    <div className="donations-list">
                        {mergedDonations.map((donation, index) => (
                            <div key={donation._id || `donor-${index}`} className="donation-item">
                                <div className="donation-number">
                                    {donation.user?.photo ? (
                                        <Image
                                            fill
                                            src={donation.user.photo}
                                            alt={`${donation.user.name} photo`}
                                            className="donor-photo"
                                            onError={(e) => {
                                                try {
                                                    const target = e?.target || e?.currentTarget;
                                                    if (!target) return;
                                                    if (target.style) target.style.display = 'none';
                                                    const parent = target.parentElement;
                                                    const fallback = parent && parent.querySelector ? parent.querySelector('.donation-sequence-fallback') : null;
                                                    if (fallback && fallback.style) fallback.style.display = 'flex';
                                                } catch (err) {
                                                    console.warn('Image onError handler failed', err);
                                                }
                                            }}
                                            style={{ objectFit: "cover", objectPosition: "top", borderRadius: "50%" }}
                                        />
                                    ) : null}
                                </div>
                                <div className="donation-details">
                                    <h3 className="donor-name"><LocalizedName user={donation.user} initial={`${donation.user?.surname?.toLowerCase()} ${donation.user?.name?.toLowerCase()} ${donation.user?.fatherName?.toLowerCase()}`} /></h3>
                                    {donation.amount > 0 && (
                                        <p className="donation-amount">{formatCurrency(donation.amount)}</p>
                                    )}
                                    {donation.services.length > 0 && (
                                        <p className="donation-service">{donation.services.join(', ')}</p>
                                    )}
                                    {/* <p className="donation-date">
                                        Donated on {new Date(donation.createdAt).toLocaleDateString('en-IN', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </p> */}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>

        <style jsx>{`
                .donations-page {
                    padding: 20px 0;
                    min-height: 100vh;
                    background: #0e82800a;
                    padding-top: 80px;
                    font-family: "Baloo Bhai 2", sans-serif;
                }

                .container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 0 20px;
                }

                .donations-header {
                    text-align: center;
                    margin-bottom: 40px;
                }

                .qr-section {
                    margin: 22px auto;
                    background: rgba(255,255,255,0.85);
                    border: 1px solid rgba(0, 135, 140, 0.2);
                    border-radius: 16px;
                    padding: 16px;
                    max-width: 360px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
                    backdrop-filter: blur(8px);
                }

                .qr-heading {
                    font-size: 1rem;
                    margin-bottom: 12px;
                    color: #0f4d48;
                    font-weight: 600;
                    line-height: 1.4;
                }

                .qr-card {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 12px;
                    border-radius: 12px;
                }

                .qr-image {
                    border-radius: 12px;
                    width: 100%;
                    height: auto;
                    max-width: 260px;
                    cursor: pointer;
                    transition: transform 0.2s ease;
                }

                .qr-image:hover {
                    transform: scale(1.03);
                }

                .qr-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.65);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .qr-modal {
                    position: relative;
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 22px;
                    max-width: 460px;
                    width: 100%;
                    text-align: center;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
                }

                .qr-modal-close {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    width: 32px;
                    height: 32px;
                    border: none;
                    background: #f44336;
                    color: white;
                    border-radius: 50%;
                    font-size: 20px;
                    font-weight: 700;
                    cursor: pointer;
                }

                .qr-modal h2 {
                    margin: 10px 0 20px;
                    font-size: 1.05rem;
                    color: #0f4d48;
                }

                .qr-modal-image {
                    width: 100%;
                    height: auto;
                    max-width: 420px;
                    border-radius: 12px;
                }

                .qr-modal p {
                    margin-top: 14px;
                    font-size: 0.95rem;
                    color: #444;
                }

                .donations-header h1 {
                    font-size: 2.5rem;
                    margin-bottom: 20px;
                }

                .donations-header p{
                    margin-bottom: 15px;
                }

                .total-amount h2 {
                    font-size: 1.8rem;
                    background: rgba(255,255,255,0.2);
                    padding: 15px 30px;
                    border-radius: 50px;
                    display: inline-block;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.3);
                    font-weight: 300;
                }

                .no-donations {
                    text-align: center;
                    background: rgba(255,255,255,0.9);
                    padding: 50px;
                    border-radius: 15px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.1);
                    color: #666;
                    font-size: 1.1rem;
                }

                .donations-list {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    margin-bottom: 40px;
                }

                .donation-item {
                    background: rgba(255,255,255,0.95);
                    border-radius: 15px;
                    padding: 25px;
                    display: flex;
                    gap: 20px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.3);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }

                .donation-item:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
                }

                .donation-number {
                    background: #00879c2a;
                    color: white;
                    width: 150px;
                    height: 150px;
                    border-radius: 50%;
                    font-weight: bold;
                    flex-shrink: 0;
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                

                .donation-sequence-fallback {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 100%;
                    height: 100%;
                    display: none;
                    align-items: center;
                    justify-content: center;
                    background: #00879c;
                    border-radius: 50%;
                }

                .donation-details {
                    flex: 1;
                }

                .donor-name {
                    margin: 0 0 8px 0;
                    font-size: 2rem;
                    color: #333;
                    font-weight: 600;
                    text-transform: capitalize;
                }

                .donation-amount {
                    margin: 0 0 8px 0;
                    font-size: 1rem;
                    font-weight: bold;
                    color: #666;
                    margin-top: 32px;
                }

                .donation-date {
                    margin: 0;
                    color: #666;
                    font-size: 0.9rem;
                }

                .donation-message {
                    background: rgba(255,255,255,0.9);
                    padding: 30px;
                    border-radius: 15px;
                    text-align: center;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.3);
                }

                .donation-message p {
                    margin: 0;
                    font-size: 1.1rem;
                    line-height: 1.6;
                    color: #333;
                }

                .loading {
                    text-align: center;
                    padding: 100px 20px;
                    color: white;
                    font-size: 1.2rem;
                }

                @media (max-width: 768px) {
                    .donations-header h1 {
                        font-size: 2rem;
                    }

                    .total-amount h2 {
                        font-size: 1.4rem;
                        padding: 12px 20px;
                    }

                    .donation-item {
                        text-align: center;
                        gap: 15px;
                    }

                    .donation-amount{
                    
                        text-align: left;
                    }

                    .donation-number {
                        align-self: center;
                    }

                    .donor-name {
                        font-size: 1.2rem;
                        text-align: left;
                    }
                        .donation-service{
                            text-align: left;
                        }

                    .donation-amount {
                        font-size: 1.4rem;
                    }
                        .donation-number{
                            width: 70px;
                            height: 70px;
                        }

                        .donation-item{
                            padding: 10px;
                        }
                            .donations-page .container{
                                padding: 0 10px;}

                                .donation-item{
                                    align-items: flex-start;
                                }

                                    .donation-number{
                                        border-radius: 50%;
                                    }
                                        .donation-number img{
                                            object-fit: cover !important;
                                        }

                                .donation-amount{
                                    margin-top: 0;
                                    }
                                    .donation-service{
                                        margin-top: 0;
                                        font-size: 1rem;
                                        font-weight: 300;
                                    }
                                        .donor-name{
                                            margin-bottom: 0;
                                        }
                                            .donation-amount{
                                                font-size: 1rem;
                                            }

                                             .donations-list {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    margin-bottom: 40px;
                }
                }
            `}</style>

    </>
}